/**
 * usePropertyTypes — справочник «Тип объекта» организации (property_types_ref).
 *
 * Раньше тип объекта был захардкожен в 5 строк ('apartment'/'villa'/'office'/
 * 'commercial'/'other') сразу в нескольких местах фронта (PropertyModal,
 * PropertiesPage — карта, попап, список; ClientDetailPage). Теперь список
 * настраивается тенантом в Настройки → Организация, поэтому лейбл и иконка —
 * данные с сервера, а не константа в коде.
 *
 * Кэш на модуле, тот же приём, что usePriceUnits: точек чтения много (форма
 * Объекта, список, карта, попап маркера, карточка Клиента), и заставлять
 * каждую тянуть список самостоятельно — N одинаковых запросов на экран.
 * Словарь меняется на порядки реже, чем читается — стоимость компромисса
 * (правка из другой вкладки подхватится после reload) осознанная.
 *
 * ⚠️ is_residential — поведенческий флаг бэкенда (видимость «Оборудования»,
 * services/equipment.js). Тенант выставляет его сам чекбоксом «Жильё» при
 * создании/правке типа (SettingsPage → PropertyTypesSection), но фронт нигде
 * не интерпретирует его сам иначе — источник истины один, в
 * services/propertyTypes.js. area_preset (пресет помещений при инспекции)
 * ручного ввода не имеет — только у дефолтов инстанса, показывается readonly.
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { track } from '../analytics';

let cache = null;          // Promise<{types, canEdit}>|null
let cachedCanEdit = false; // последнее известное значение — для мгновенного возврата новым подписчикам

function load() {
  if (!cache) {
    cache = api.get('/properties/types-ref')
      .then(r => {
        cachedCanEdit = !!r.can_edit;
        return r.types || [];
      })
      .catch(() => []);
  }
  return cache;
}

/** Сбросить кэш — после добавления/переименования/архивации типа. */
export function invalidatePropertyTypes() {
  cache = null;
}

export function usePropertyTypes({ enabled = true } = {}) {
  const [types, setTypes] = useState([]);
  const [canEdit, setCanEdit] = useState(cachedCanEdit);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setLoading(true);
    load().then(t => {
      if (!alive) return;
      setTypes(t || []);
      setCanEdit(cachedCanEdit);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [enabled]);

  const reload = async () => {
    invalidatePropertyTypes();
    const t = await load();
    setTypes(t || []);
    setCanEdit(cachedCanEdit);
    return t;
  };

  const addType = async (label, isResidential = false) => {
    const r = await api.post('/properties/types-ref', { label_ru: label, is_residential: !!isResidential });
    track('property_type_edit', { action: r.created ? 'add' : 'reuse', is_residential: !!isResidential });
    await reload();
    return r.type;
  };

  const renameType = async (typeId, label, isResidential) => {
    const body = { label_ru: label };
    if (isResidential !== undefined) body.is_residential = !!isResidential;
    const row = await api.put(`/properties/types-ref/${typeId}`, body);
    track('property_type_edit', { action: 'rename', is_residential: !!isResidential });
    invalidatePropertyTypes();
    setTypes(prev => prev.map(t => (t.id === typeId ? { ...t, ...row } : t)));
    return row;
  };

  const archiveType = async (typeId) => {
    await api.delete(`/properties/types-ref/${typeId}`);
    track('property_type_edit', { action: 'archive' });
    invalidatePropertyTypes();
    setTypes(prev => prev.filter(t => t.id !== typeId));
  };

  // Драг-н-дроп в Настройках. Optimistic: стейт уже переставлен вызывающим
  // (SortableContext даёт готовый reordered-массив), здесь только шлём его
  // на сервер и держим кэш модуля в согласии — иначе следующий читатель
  // (форма Объекта и т.п.) увидит старый порядок до перезагрузки.
  const reorderTypes = async (reordered) => {
    setTypes(reordered);
    try {
      await api.patch('/properties/types-ref/reorder', { ids: reordered.map(t => t.id) });
      track('property_type_edit', { action: 'reorder' });
      invalidatePropertyTypes();
    } catch (e) {
      await reload(); // откат к серверному порядку
      throw e;
    }
  };

  return { types, loading, canEdit, reload, addType, renameType, archiveType, reorderTypes };
}

/** Ярлык типа на языке интерфейса с честным фолбэком на второй. */
export function propertyTypeLabel(row, lang) {
  if (!row) return '';
  return (lang === 'en' ? (row.label_en || row.label_ru) : (row.label_ru || row.label_en)) || '';
}

/** Лейбл по ключу типа (properties.type) — для списков/карточек Объекта. */
export function propertyTypeLabelByKey(types, key, lang) {
  if (!key) return '';
  return propertyTypeLabel((types || []).find(t => t.key === key), lang);
}
